/**
 * Panel "Destilado" — Fase 27 §4 Fase 4.
 *
 * Resumen del job session.distill para una sesión:
 *   - badge de modo (oneshot|agentic)
 *   - tarjeta total: duración · costo · facts
 *   - lista de distillers / tools (depende del modo)
 *   - botones de A/B re-run
 *   - link "Ver en waterfall" → selecciona el span del job
 *
 * Diseño minimalista — reusa `.card`, `.badge`, `.kind-pill` del global.css.
 */
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchSessionDistill,
  redistillSession,
  type DistillerSummary,
  type DistillToolCallSummary,
  type SessionDistillSummary,
} from '../../api/admin/distill.js';
import { formatDurationMs, formatNumber, nanoToUsdCompact } from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';

export interface DistillPanelProps {
  sessionId: string;
  onJumpToWaterfall: (spanId: string) => void;
}

function ModeBadge({ mode }: { mode: 'oneshot' | 'agentic' }): ReactElement {
  const label = mode === 'agentic' ? 'agentic · beta' : 'oneshot';
  const cls = mode === 'agentic' ? 'badge b-warn' : 'badge b-info';
  return <span className={cls} data-mode={mode}>{label}</span>;
}

function ToolsList({ tools }: { tools: DistillToolCallSummary[] }): ReactElement {
  if (tools.length === 0) {
    return <div className="t-meta">No tool calls registradas.</div>;
  }
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
      {tools.map((t) => (
        <li
          key={t.name}
          className="row"
          style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
        >
          <span className="mono">{t.name}</span>
          <span className="t-meta">×{t.count}</span>
        </li>
      ))}
    </ul>
  );
}

function DistillersTable({
  distillers,
  onJumpToWaterfall,
}: {
  distillers: DistillerSummary[];
  onJumpToWaterfall: (spanId: string) => void;
}): ReactElement {
  if (distillers.length === 0) {
    return <div className="t-meta">Sin distillers ejecutados.</div>;
  }
  return (
    <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Distiller</th>
          <th style={{ textAlign: 'right' }}>Duración</th>
          <th style={{ textAlign: 'right' }}>Costo</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {distillers.map((d) => (
          <tr key={d.spanId}>
            <td className="mono">{d.name}</td>
            <td style={{ textAlign: 'right' }}>{formatDurationMs(d.durationMs)}</td>
            <td style={{ textAlign: 'right' }}>
              {(() => {
                const c = preferRawCost(d.providerCostNano, d.costNano);
                return c ? nanoToUsdCompact(c) : '—';
              })()}
            </td>
            <td style={{ textAlign: 'right' }}>
              <button
                type="button"
                className="btn"
                onClick={() => onJumpToWaterfall(d.spanId)}
                title="Ir a este span en el waterfall"
              >
                ↳
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SummaryHeader({
  summary,
  onJumpToWaterfall,
}: {
  summary: SessionDistillSummary;
  onJumpToWaterfall: (spanId: string) => void;
}): ReactElement {
  return (
    <div
      className="distill-summary-row"
      style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
    >
      <ModeBadge mode={summary.mode} />
      <span className="t-meta">
        <span className="key">duración</span>{' '}
        <span className="val">{formatDurationMs(summary.totalDurationMs)}</span>
      </span>
      <span className="t-meta">
        <span className="key">costo</span>{' '}
        <span className="val">
          {nanoToUsdCompact(preferRawCost(summary.totalCostRawNano, summary.totalCostNano))}
        </span>
      </span>
      <span className="t-meta">
        <span className="key">facts</span>{' '}
        <span className="val">{formatNumber(summary.factsAccepted)}</span>
      </span>
      {summary.delayMs !== null ? (
        <span className="t-meta">
          <span className="key">delay</span>{' '}
          <span className="val">{formatDurationMs(summary.delayMs)}</span>
        </span>
      ) : null}
      <button
        type="button"
        className="btn"
        onClick={() => onJumpToWaterfall(summary.jobSpanId)}
        style={{ marginLeft: 'auto' }}
      >
        Ver en waterfall
      </button>
    </div>
  );
}

export function DistillPanel({
  sessionId,
  onJumpToWaterfall,
}: DistillPanelProps): ReactElement {
  const qc = useQueryClient();
  const [redistillMode, setRedistillMode] = useState<'oneshot' | 'agentic' | null>(null);
  const [redistillError, setRedistillError] = useState<string | null>(null);
  const [redistillJobId, setRedistillJobId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'distill', sessionId],
    queryFn: () => fetchSessionDistill(sessionId),
    enabled: Boolean(sessionId),
  });

  const mutation = useMutation({
    mutationFn: (mode: 'oneshot' | 'agentic') => redistillSession(sessionId, mode),
    onMutate: (mode) => {
      setRedistillMode(mode);
      setRedistillError(null);
      setRedistillJobId(null);
    },
    onSuccess: (data) => {
      setRedistillMode(null);
      setRedistillJobId(data.jobId);
      // Refrescamos el resumen tras un beat — el job tarda en ejecutarse.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['admin', 'distill', sessionId] });
        void qc.invalidateQueries({ queryKey: ['admin', 'timeline', sessionId] });
      }, 3000);
    },
    onError: (err: unknown) => {
      setRedistillMode(null);
      setRedistillError(err instanceof Error ? err.message : 'redistill_failed');
    },
  });

  if (query.isPending) {
    return (
      <div className="card" style={{ padding: 16 }}>
        <div className="t-meta">Cargando destilado…</div>
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="card" style={{ padding: 16 }}>
        <div className="t-meta">Error: {(query.error).message}</div>
      </div>
    );
  }
  const data = query.data.distill;
  if (!data) {
    return (
      <div className="card" style={{ padding: 16 }}>
        <div className="t-meta">Esta sesión todavía no tiene destilado ejecutado.</div>
        <p className="t-meta" style={{ marginTop: 8 }}>
          El job <code className="mono">session.distill</code> corre best-effort tras{' '}
          <code className="mono">session_end</code>. Si la sesión sigue activa, no hay
          aún.
        </p>
      </div>
    );
  }

  return (
    <section className="card" data-testid="distill-panel" style={{ padding: 16, display: 'grid', gap: 12 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Destilado</h3>
        <Link
          to={`/distill/${sessionId}`}
          className="btn"
          style={{ marginLeft: 'auto' }}
          data-testid="distill-detail-link"
        >
          Ver detalle del destilado →
        </Link>
      </header>

      <SummaryHeader summary={data} onJumpToWaterfall={onJumpToWaterfall} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div className="section-eyebrow" style={{ marginBottom: 8 }}>
            Distillers
          </div>
          <DistillersTable
            distillers={data.distillers}
            onJumpToWaterfall={onJumpToWaterfall}
          />
        </div>
        {data.mode === 'agentic' ? (
          <div>
            <div className="section-eyebrow" style={{ marginBottom: 8 }}>
              Tools llamadas
            </div>
            <ToolsList tools={data.toolsCalled} />
          </div>
        ) : null}
      </div>

      <div className="distill-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span className="t-meta">A/B re-run:</span>
        <button
          type="button"
          className="btn"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate('oneshot')}
        >
          {redistillMode === 'oneshot' ? 'Encolando…' : 'Re-correr oneshot'}
        </button>
        <button
          type="button"
          className="btn"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate('agentic')}
        >
          {redistillMode === 'agentic' ? 'Encolando…' : 'Re-correr agentic'}
        </button>
        {redistillJobId ? (
          <span className="t-meta">
            Encolado job <code className="mono">{redistillJobId.slice(0, 8)}</code>. Refresco en
            ~3s.
          </span>
        ) : null}
        {redistillError ? (
          <span className="t-meta" style={{ color: 'var(--danger)' }}>
            {redistillError}
          </span>
        ) : null}
      </div>
    </section>
  );
}
